import { useAuth } from "@clerk/clerk-react";
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import {
  Campaign,
  CampaignBrand,
  CampaignStats,
  CampaignStatus,
} from "schemas/dashboard";

import { fetcherAuth } from "utils/api";
import { isDemoUser } from "utils/demo";

import { AlertContext } from "./Alert";
import { OrganizationUserContext } from "./Organization";

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

export const CAMPAIGNS_PER_PAGE = 10;

export interface GetCampaignResponse {
  campaign: Campaign;
  campaignBrands: CampaignBrand[];
  brandsCount: number;
}

interface CampaignsContextInterface {
  campaigns: Campaign[];
  setCampaigns: Dispatch<SetStateAction<Campaign[]>>;
  campaignPage: number;
  setCampaignPage: Dispatch<SetStateAction<number>>;
  campaignsCount: number | null;
  setCampaignsCount: Dispatch<SetStateAction<number | null>>;
  getCampaigns: (page?: number) => Promise<void>;
  campaignsLoading: boolean;
  setCampaignsLoading: Dispatch<SetStateAction<boolean>>;
  campaignStats: { [key in number]: CampaignStats };
  campaignStatsLoading: { [key in number]: boolean };
  deleteCampaign: (campaignIds: number[]) => Promise<void>;
  addCampaign: (campaign: Campaign) => void;
  getCampaign: (
    campaignId: number,
    page?: number,
    perPage?: number,
  ) => Promise<GetCampaignResponse | null>;
}

const defaultInterface = {
  campaigns: [],
  setCampaigns: defaultContextMissingFunction,
  campaignPage: 1,
  setCampaignPage: defaultContextMissingFunction,
  campaignsCount: null,
  setCampaignsCount: defaultContextMissingFunction,
  getCampaigns: defaultContextMissingFunction,
  campaignsLoading: false,
  setCampaignsLoading: defaultContextMissingFunction,
  campaignStats: {},
  campaignStatsLoading: {},
  deleteCampaign: defaultContextMissingFunction,
  addCampaign: defaultContextMissingFunction,
  getCampaign: defaultContextMissingFunction,
};

const DEFAULT_BRAND_DEMO_CAMPAIGNS = [
  {
    id: 0,
    name: "Beauty Creators",
    createdAt: 0,
    status: CampaignStatus.in_progress,
    daysOfWeek: [0, 1, 2, 3],
    timezone: "America/New_York",
    startDate: "Mon, 20 Oct 2025 00:00:00 GMT",
    endDate: "Thu, 06 Nov 2025 00:00:00 GMT",
    startTime: "09:00",
    endTime: "17:00",
    createdByAdmin: false,
    campaignBrandCount: 120,
  },
  {
    id: 1,
    name: "Health/Wellness Creators",
    createdAt: 0,
    status: CampaignStatus.in_progress,
    daysOfWeek: [0, 1, 2, 3],
    timezone: "America/New_York",
    startDate: "Mon, 13 Oct 2025 00:00:00 GMT",
    endDate: "Thu, 20 Nov 2025 00:00:00 GMT",
    startTime: "09:00",
    endTime: "17:00",
    createdByAdmin: false,
    campaignBrandCount: 110,
  },
  {
    id: 2,
    name: "Fitness Creators",
    createdAt: 0,
    status: CampaignStatus.in_progress,
    daysOfWeek: [0, 1, 2, 3],
    timezone: "America/New_York",
    startDate: "Mon, 6 Oct 2025 00:00:00 GMT",
    endDate: "Thu, 13 Nov 2025 00:00:00 GMT",
    startTime: "09:00",
    endTime: "17:00",
    createdByAdmin: false,
    campaignBrandCount: 118,
  },
];

const DEFAULT_CAMPAIGN_STATS: { [key in number]: CampaignStats } = {
  0: {
    followUpCount: 0,
    openedCount: 90,
    openedPercentage: 0.75,
    outreachCount: 120,
    replyCount: 0,
    respondedCount: 20,
    respondedPercentage: 0.1666,
  },
  1: {
    followUpCount: 0,
    openedCount: 75,
    openedPercentage: 0.6818,
    outreachCount: 110,
    replyCount: 0,
    respondedCount: 14,
    respondedPercentage: 0.1273,
  },
  2: {
    followUpCount: 0,
    openedCount: 87,
    openedPercentage: 0.7373,
    outreachCount: 118,
    replyCount: 0,
    respondedCount: 20,
    respondedPercentage: 0.1695,
  },
};

const CampaignsContext =
  createContext<CampaignsContextInterface>(defaultInterface);

interface CampaignsProviderProps {
  children: React.ReactNode;
}

const CampaignsProvider = ({ children }: CampaignsProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignPage, setCampaignPage] = useState<number>(1);
  const [campaignStats, setCampaignStats] = useState<{
    [key in number]: CampaignStats;
  }>({});
  const [campaignsCount, setCampaignsCount] = useState<number | null>(null);
  const [campaignsLoading, setCampaignsLoading] = useState<boolean>(false);
  const [campaignStatsLoading, setCampaignStatsLoading] = useState<{
    [key in number]: boolean;
  }>({});

  const getCampaignStats = async (campaignIds: number[]) => {
    if (campaignIds.length === 0 || !currentOrg?.id) {
      return;
    }

    setCampaignStatsLoading((prev) => ({
      ...prev,
      ...campaignIds.reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    }));

    const params = new URLSearchParams();
    campaignIds.forEach((id) => params.append("campaign_ids", id.toString()));
    const url = `/api/organization/${
      currentOrg?.id
    }/campaigns/stats?${params.toString()}`;

    try {
      const { campaignStats } = await fetcherAuth(getToken, url, "GET");
      const statsById = campaignIds.reduce(
        (acc, id, index) => {
          acc[id] = campaignStats[index];
          return acc;
        },
        {} as Record<number, CampaignStats>,
      );
      setCampaignStats((prev) => ({
        ...prev,
        ...statsById,
      }));
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCampaignStatsLoading((prev) => ({
        ...prev,
        ...campaignIds.reduce(
          (acc, id) => {
            acc[id] = false;
            return acc;
          },
          {} as Record<number, boolean>,
        ),
      }));
    }
  };

  const getCampaigns = async (page: number = 1) => {
    if (!currentOrg?.id) {
      return;
    }

    if (currentOrg.isBrand && isDemoUser(currentOrg)) {
      setCampaignsLoading(false);

      setCampaigns(DEFAULT_BRAND_DEMO_CAMPAIGNS);
      setCampaignStats(DEFAULT_CAMPAIGN_STATS);

      return;
    }

    setCampaignsLoading(true);
    setCampaignPage(page);
    const url = `/api/organization/${currentOrg?.id}/campaigns?page=${page}&per_page=${CAMPAIGNS_PER_PAGE}`;

    try {
      const { campaigns, count } = await fetcherAuth(getToken, url, "GET");
      setCampaigns(campaigns);
      getCampaignStats(campaigns.map((c: Campaign) => c.id));
      setCampaignsCount(count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const refetchCampaigns = () => {
    getCampaigns(1);
  };

  const addCampaign = (campaign: Campaign) => {
    setCampaigns((prev) => [...prev, campaign]);
    if (campaign?.id) getCampaignStats([campaign.id]);
  };

  const deleteCampaign = async (campaignIds: number[]) => {
    if (!currentOrg?.id) {
      return;
    }

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/delete`,
        "POST",
        {},
        { campaignIds },
      );
      refetchCampaigns();
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getCampaign = async (
    campaignId: number,
    page: number = 1,
    perPage: number = 3,
  ) => {
    if (!currentOrg?.id) {
      return null;
    }

    try {
      const { campaign, campaignBrands, brandsCount } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/${campaignId}?page=${page}&per_page=${perPage}`,
        "GET",
      );
      return { campaign, campaignBrands, brandsCount };
    } catch (error) {
      setErrorAlert(error);
    }
    return null;
  };

  return (
    <CampaignsContext.Provider
      value={{
        campaigns,
        setCampaigns,
        campaignPage,
        setCampaignPage,
        campaignsCount,
        setCampaignsCount,
        getCampaigns,
        campaignsLoading,
        setCampaignsLoading,
        campaignStats,
        campaignStatsLoading,
        deleteCampaign,
        addCampaign,
        getCampaign,
      }}
    >
      {children}
    </CampaignsContext.Provider>
  );
};

export { CampaignsProvider, CampaignsContext };
